import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getWorkEntriesReport,
  clearReport,
} from "../../redux/workEntrySlice";
import { getAllEmps } from "../../redux/empSlice";
import SearchableSelect from "../common/SearchableSelect";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Helmet } from "react-helmet-async";

const WorkReport = () => {
  const dispatch = useDispatch();
  const { report, loading, error } = useSelector((state) => state.workEntry);
  const { emps } = useSelector((state) => state.emp);

  const [filters, setFilters] = useState({
    fromDate: new Date(new Date().setDate(1)).toISOString().split("T")[0], // First day of current month
    toDate: new Date().toISOString().split("T")[0], // Today
    employeeId: "",
  });

  useEffect(() => {
    dispatch(getAllEmps());
  }, [dispatch]);

  // Clear report on unmount
  useEffect(() => {
    return () => {
      dispatch(clearReport());
    };
  }, [dispatch]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleQuickPreset = (preset) => {
    const today = new Date();
    let fromDate, toDate;

    switch (preset) {
      case "lastMonth":
        fromDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        toDate = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      case "thisMonth":
        fromDate = new Date(today.getFullYear(), today.getMonth(), 1);
        toDate = today;
        break;
      case "custom":
        // User will set manually
        return;
      default:
        return;
    }

    setFilters((prev) => ({
      ...prev,
      fromDate: fromDate.toISOString().split("T")[0],
      toDate: toDate.toISOString().split("T")[0],
    }));
  };

  const handleSearch = async () => {
    if (!filters.fromDate || !filters.toDate) {
      alert("Please select both from date and to date");
      return;
    }

    const params = {
      fromDate: filters.fromDate,
      toDate: filters.toDate,
    };

    if (filters.employeeId) {
      params.employeeId = filters.employeeId;
    }

    try {
      await dispatch(getWorkEntriesReport(params)).unwrap();
    } catch (error) {
      // Error handled by Redux
    }
  };

  // Generate PDF and return as blob
  const generatePDFBlob = () => {
    if (!report || !report.rows || report.rows.length === 0) {
      throw new Error("No data available");
    }

    const doc = new jsPDF();

    // Header
    doc.setFontSize(18);
    doc.text("SHREE HAREKRISHNA GAAJ CENTER", 105, 15, { align: "center" });
    doc.setFontSize(14);
    doc.text("Work Report", 105, 25, { align: "center" });

    // Report details
    doc.setFontSize(10);
    let reportInfo = "";
    if (filters.employeeId) {
      const selectedEmp = emps.find((e) => e._id === filters.employeeId);
      reportInfo = `Employee: ${selectedEmp?.name || "N/A"}`;
    } else {
      reportInfo = "All Employees";
    }

    const dateInfo = `Date: ${new Date(filters.fromDate).toLocaleDateString()} to ${new Date(filters.toDate).toLocaleDateString()}`;
    const totalEntries = `Total Entries: ${report.rows.length}`;

    doc.text(reportInfo, 14, 35);
    doc.text(dateInfo, 14, 40);
    doc.text(totalEntries, 14, 45);
    doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, 50);

    // Table data - only Date, Employee, Quantity, Rate, Amount
    const tableData = report.rows.map((row) => [
      new Date(row.date).toLocaleDateString(),
      row.employeeName || "N/A",
      parseFloat(row.quantity || 0).toFixed(2),
      `Rs.${parseFloat(row.rate || 0).toFixed(2)}`,
      `Rs.${parseFloat(row.total || 0).toFixed(2)}`,
    ]);

    // Create table
    autoTable(doc, {
      startY: 55,
      head: [["DATE", "EMPLOYEE", "QUANTITY", "RATE", "AMOUNT"]],
      body: tableData,
      styles: { fontSize: 9, halign: "center" },
      headStyles: { fillColor: [102, 126, 234], halign: "center", textColor: [255, 255, 255] },
      alternateRowStyles: { fillColor: [245, 247, 250] },
      columnStyles: {
        0: { cellWidth: 30, halign: "center" }, // DATE
        1: { cellWidth: 50, halign: "left" }, // EMPLOYEE
        2: { cellWidth: 25, halign: "center" }, // QUANTITY
        3: { cellWidth: 30, halign: "center" }, // RATE
        4: { cellWidth: 30, halign: "center" }, // AMOUNT
      },
    });

    // Add totals section - centered
    const tableFinalY = doc.lastAutoTable?.finalY;
    const finalY = tableFinalY ? tableFinalY + 15 : 200;

    // Calculate page width for centering
    const pageWidth = doc.internal.pageSize.getWidth();

    if (filters.employeeId && report.employeeTotal > 0) {
      doc.setFontSize(11);
      const employeeTotalText = `Employee Total: Rs.${parseFloat(report.employeeTotal).toFixed(2)}`;
      const employeeTotalWidth = doc.getTextWidth(employeeTotalText);
      doc.text(employeeTotalText, (pageWidth - employeeTotalWidth) / 2, finalY, { align: "center" });
      
      doc.setFontSize(12);
      doc.setFont(undefined, "bold");
      const grandTotalText = `Grand Total: Rs.${parseFloat(report.grandTotal).toFixed(2)}`;
      const grandTotalWidth = doc.getTextWidth(grandTotalText);
      doc.text(grandTotalText, (pageWidth - grandTotalWidth) / 2, finalY + 10, { align: "center" });
      doc.setFont(undefined, "normal");
    } else {
      doc.setFontSize(12);
      doc.setFont(undefined, "bold");
      const grandTotalText = `Grand Total: Rs.${parseFloat(report.grandTotal).toFixed(2)}`;
      const grandTotalWidth = doc.getTextWidth(grandTotalText);
      doc.text(grandTotalText, (pageWidth - grandTotalWidth) / 2, finalY, { align: "center" });
      doc.setFont(undefined, "normal");
    }

    // Return PDF as blob
    return doc.output("blob");
  };

  // Generate PDF and download
  const generatePDF = () => {
    if (!report || !report.rows || report.rows.length === 0) {
      alert("No data available to download");
      return;
    }

    try {
      const blob = generatePDFBlob();
      const fileName = `Work_Report_${filters.fromDate}_to_${filters.toDate}.pdf`;
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      alert("Error generating PDF: " + error.message);
    }
  };

  // Share PDF via WhatsApp
  const shareViaWhatsApp = async () => {
    if (!report || !report.rows || report.rows.length === 0) {
      alert("No data available to share");
      return;
    }

    try {
      const blob = generatePDFBlob();
      const fileName = `Work_Report_${filters.fromDate}_to_${filters.toDate}.pdf`;
      
      // Create message text
      let message = "📄 Work Report\n\n";
      
      if (filters.employeeId) {
        const selectedEmp = emps.find((e) => e._id === filters.employeeId);
        message += `Employee: ${selectedEmp?.name || "N/A"}\n`;
      } else {
        message += `All Employees\n`;
      }
      
      message += `Date: ${new Date(filters.fromDate).toLocaleDateString()} to ${new Date(filters.toDate).toLocaleDateString()}\n`;
      message += `Total Entries: ${report.rows.length}\n`;
      
      if (filters.employeeId && report.employeeTotal > 0) {
        message += `Employee Total: ₹${report.employeeTotal.toFixed(2)}\n`;
      }
      message += `Grand Total: ₹${report.grandTotal.toFixed(2)}`;
      message += `\n\nPlease find the attached PDF report.`;
      
      // Check if Web Share API is available (works on mobile and some desktop browsers)
      if (navigator.share) {
        try {
          // Convert blob to File
          const file = new File([blob], fileName, { type: "application/pdf" });
          
          // Check if we can share files
          if (navigator.canShare && navigator.canShare({ files: [file] })) {
            await navigator.share({
              files: [file],
              title: "Work Report",
              text: message,
            });
            return;
          } else if (navigator.canShare && navigator.canShare({ text: message })) {
            // If file sharing not supported, try text sharing
            await navigator.share({
              text: message,
              title: "Work Report",
            });
            // Download file separately
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            return;
          }
        } catch (shareError) {
          // If share fails, continue to WhatsApp Web fallback
          console.log("Web Share API failed, using WhatsApp Web:", shareError);
        }
      }
      
      // Desktop fallback: Open WhatsApp Web with message
      const encodedMessage = encodeURIComponent(message);
      
      // Download the file first so user can attach it
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Open WhatsApp Web with pre-filled message
      // Use wa.me which works better across devices
      const whatsappUrl = `https://wa.me/?text=${encodedMessage}`;
      
      // Try to open in new tab
      try {
        window.open(whatsappUrl, "_blank", "noopener,noreferrer");
      } catch (error) {
        // If popup blocked, redirect current window
        window.location.href = whatsappUrl;
      }
      
      // Clean up URL after a delay
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      
    } catch (error) {
      console.error("Error sharing via WhatsApp:", error);
      // Fallback: Just download the PDF
      generatePDF();
    }
  };

  return (
    <>
      <Helmet>
        <title>Work Report - RTS Books</title>
      </Helmet>
      <style>{`
        .work-report-container {
          min-height: 100vh;
          background: linear-gradient(135deg, #f0f4ff 0%, #e0e7ff 50%, #c7d2fe 100%);
          padding: 20px;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
        }

        .work-report-wrapper {
          max-width: 1400px;
          margin: 0 auto;
        }

        .work-report-header {
          text-align: center;
          margin-bottom: 30px;
          color: #1e40af;
        }

        .work-report-title {
          font-size: 2.5rem;
          font-weight: 700;
          margin-bottom: 10px;
          color: #1e40af;
          letter-spacing: -0.5px;
        }

        .work-report-card {
          background: #ffffff;
          border-radius: 16px;
          padding: 30px;
          box-shadow: 0 4px 20px rgba(30, 64, 175, 0.08);
          border: 1px solid #e0e7ff;
          margin-bottom: 20px;
        }

        .filters-section {
          margin-bottom: 30px;
        }

        .filters-row {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 15px;
          margin-bottom: 15px;
        }

        .filter-group label {
          display: block;
          margin-bottom: 8px;
          font-weight: 600;
          color: #333;
          font-size: 0.95rem;
        }

        .filter-group input,
        .filter-group select {
          width: 100%;
          padding: 12px 16px;
          border: 2px solid #e1e5e9;
          border-radius: 8px;
          font-size: 1rem;
        }

        .quick-presets {
          display: flex;
          gap: 10px;
          margin-bottom: 15px;
          flex-wrap: wrap;
        }

        .preset-btn {
          padding: 8px 16px;
          border: 2px solid #667eea;
          background: white;
          color: #667eea;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 500;
          transition: all 0.3s ease;
          font-size: 0.9rem;
        }

        .preset-btn:hover {
          background: #667eea;
          color: white;
        }

        .btn {
          padding: 12px 24px;
          border: none;
          border-radius: 8px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          white-space: nowrap;
        }

        .btn-primary {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
        }

        .btn-success {
          background: #28a745;
          color: white;
        }

        .btn-whatsapp {
          background: #25D366;
          color: white;
          box-shadow: 0 2px 8px rgba(37, 211, 102, 0.2);
        }

        .btn-whatsapp:hover {
          background: #20BA5A;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(37, 211, 102, 0.3);
        }

        .btn-danger {
          background: #dc3545;
          color: white;
        }

        .btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        .btn:disabled:hover {
          transform: none;
        }

        .btn-actions {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
        }

        .report-table-wrapper {
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
          margin: 0 -30px;
          padding: 0 30px;
        }

        .report-table {
          width: 100%;
          min-width: 800px;
          border-collapse: collapse;
          margin-top: 20px;
        }

        .report-table th,
        .report-table td {
          padding: 12px;
          text-align: left;
          border-bottom: 1px solid #e1e5e9;
        }

        .report-table th {
          background: #f8f9fa;
          font-weight: 600;
          color: #333;
          position: sticky;
          top: 0;
          z-index: 1;
        }

        .report-table tr:hover {
          background: #f8f9fa;
        }

        .report-table td small {
          display: block;
          font-size: 0.85em;
          color: #666;
          margin-top: 4px;
        }

        .totals-section {
          margin-top: 30px;
          padding-top: 20px;
          border-top: 2px solid #e1e5e9;
        }

        .total-row {
          display: flex;
          justify-content: space-between;
          padding: 15px;
          font-size: 1.1rem;
          font-weight: 600;
          align-items: center;
        }

        .total-row.employee-total {
          background: #e7f3ff;
          color: #0056b3;
          border-radius: 8px;
          margin-bottom: 10px;
        }

        .total-row.grand-total {
          background: #667eea;
          color: white;
          border-radius: 8px;
        }

        .message-error {
          padding: 15px;
          background: #f8d7da;
          color: #721c24;
          border-radius: 8px;
          margin-bottom: 20px;
          font-size: 0.95rem;
        }

        /* Tablet Responsive */
        @media (max-width: 1024px) {
          .work-report-container {
            padding: 15px;
          }

          .work-report-card {
            padding: 20px;
          }

          .work-report-title {
            font-size: 2rem;
          }

          .filters-row {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        /* Mobile Responsive */
        @media (max-width: 768px) {
          .work-report-container {
            padding: 10px;
          }

          .work-report-card {
            padding: 15px;
            border-radius: 15px;
          }

          .work-report-header {
            margin-bottom: 20px;
          }

          .work-report-title {
            font-size: 1.8rem;
            margin-bottom: 8px;
          }

          .filters-section {
            margin-bottom: 20px;
          }

          .filters-row {
            grid-template-columns: 1fr;
            gap: 12px;
            margin-bottom: 12px;
          }

          .filter-group label {
            font-size: 0.9rem;
            margin-bottom: 6px;
          }

          .filter-group input,
          .filter-group select {
            padding: 10px 0px;
            font-size: 0.95rem;
          }

          .quick-presets {
            gap: 8px;
            margin-bottom: 12px;
          }

          .preset-btn {
            padding: 8px 14px;
            font-size: 0.85rem;
            flex: 1;
            min-width: calc(50% - 4px);
          }

          .btn {
            padding: 10px 16px;
            font-size: 0.9rem;
            flex: 1;
            min-width: calc(50% - 5px);
          }

          .btn-actions {
            gap: 8px;
          }

          .report-table-wrapper {
            margin: 0 -15px;
            padding: 0 15px;
          }

          .report-table {
            min-width: 700px;
            font-size: 0.85rem;
          }

          .report-table th,
          .report-table td {
            padding: 8px 6px;
            font-size: 0.85rem;
          }

          .totals-section {
            margin-top: 20px;
            padding-top: 15px;
          }

          .total-row {
            padding: 12px;
            font-size: 1rem;
            flex-wrap: wrap;
            gap: 8px;
          }

          .total-row span {
            width: 100%;
            text-align: center;
          }

          .total-row span:last-child {
            font-size: 1.1rem;
          }
        }

        /* Small Mobile */
        @media (max-width: 480px) {
          .work-report-container {
            padding: 8px;
          }

          .work-report-card {
            padding: 12px;
          }

          .work-report-title {
            font-size: 1.5rem;
          }

          .preset-btn {
            font-size: 0.8rem;
            padding: 7px 12px;
          }

          .btn {
            padding: 10px 14px;
            font-size: 0.85rem;
          }

          .report-table {
            min-width: 600px;
            font-size: 0.8rem;
          }

          .report-table th,
          .report-table td {
            padding: 6px 4px;
            font-size: 0.8rem;
          }

          .total-row {
            padding: 10px;
            font-size: 0.95rem;
          }
        }

        /* Extra Small Mobile */
        @media (max-width: 360px) {
          .work-report-title {
            font-size: 1.3rem;
          }

          .report-table {
            min-width: 550px;
          }

          .btn {
            font-size: 0.8rem;
            padding: 8px 12px;
          }
        }
      `}</style>

      <div className="work-report-container">
        <div className="work-report-wrapper">
          <div className="work-report-header">
            <h1 className="work-report-title">Work Report</h1>
          </div>

          <div className="work-report-card">
            {error && (
              <div className="message-error">{error}</div>
            )}

            <div className="filters-section">
              <div className="quick-presets">
                <button
                  className="preset-btn"
                  onClick={() => handleQuickPreset("lastMonth")}
                >
                  Last Month
                </button>
                <button
                  className="preset-btn"
                  onClick={() => handleQuickPreset("thisMonth")}
                >
                  This Month
                </button>
              </div>

              <div className="filters-row">
                <div className="filter-group">
                  <label>From Date *</label>
                  <input
                    type="date"
                    name="fromDate"
                    value={filters.fromDate}
                    onChange={handleFilterChange}
                  />
                </div>
                <div className="filter-group">
                  <label>To Date *</label>
                  <input
                    type="date"
                    name="toDate"
                    value={filters.toDate}
                    onChange={handleFilterChange}
                  />
                </div>
                <div className="filter-group">
                  <label>Employee (Optional)</label>
                  <SearchableSelect
                    name="employeeId"
                    options={[{ _id: "", name: "All Employees", number: "" }, ...emps]}
                    value={filters.employeeId}
                    onChange={(e) => {
                      setFilters((prev) => ({ ...prev, employeeId: e.target.value }));
                    }}
                    placeholder="Search employee by name or ID..."
                    getOptionLabel={(emp) => 
                      emp._id === "" 
                        ? "All Employees" 
                        : `${emp.name} (${emp.number})`
                    }
                    getOptionValue={(emp) => emp._id || ""}
                    searchFields={["name", "number", "_id"]}
                  />
                </div>
              </div>

              <div className="btn-actions">
                <button className="btn btn-primary" onClick={handleSearch} disabled={loading}>
                  {loading ? "Loading..." : "🔍 Search"}
                </button>
                {report && report.rows && report.rows.length > 0 && (
                  <>
                    <button className="btn btn-success" onClick={generatePDF} disabled={loading}>
                      📥 Download PDF
                    </button>
                    <button className="btn btn-whatsapp" onClick={shareViaWhatsApp} disabled={loading}>
                      📱 Share on WhatsApp
                    </button>
                  </>
                )}
              </div>
            </div>

            {loading && (
              <div style={{ textAlign: "center", padding: "40px" }}>
                Loading...
              </div>
            )}

            {report && report.rows && report.rows.length > 0 && (
              <>
                <div className="report-table-wrapper">
                  <table className="report-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Employee</th>
                        <th>Work</th>
                        <th>Quantity</th>
                        <th>Rate</th>
                        <th>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.rows.map((row, index) => (
                        <tr key={index}>
                          <td>
                            {new Date(row.date).toLocaleDateString()}
                          </td>
                          <td>
                            {row.employeeName}
                            <br />
                            <small style={{ color: "#666" }}>
                              {row.employeeNumber}
                            </small>
                          </td>
                          <td>{row.workName}</td>
                          <td>{row.quantity}</td>
                          <td>₹{row.rate.toFixed(2)}</td>
                          <td>₹{row.total.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="totals-section">
                  {filters.employeeId && report.employeeTotal > 0 && (
                    <div className="total-row employee-total">
                      <span>Employee Total:</span>
                      <span>₹{report.employeeTotal.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="total-row grand-total">
                    <span>Grand Total:</span>
                    <span>₹{report.grandTotal.toFixed(2)}</span>
                  </div>
                </div>
              </>
            )}

            {report && report.rows && report.rows.length === 0 && (
              <div style={{ textAlign: "center", padding: "40px", color: "#666" }}>
                No entries found for the selected criteria
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default WorkReport;